import React from 'react'
import PropTypes from 'prop-types'
import exact from 'prop-types-exact'
import * as Yup from 'yup'
import { AssetField } from 'components'
import { StepForm } from 'forms'
import { FieldArray, useFormikContext } from 'formik'
import { HOUSEHOLD_ASSETS_QUESTIONS, HOUSEHOLD_ASSETS_GROUPS } from 'config'
import { findIndex } from 'lodash'
import { checkmark } from 'images'
import classNames from 'classnames'

// Asset types that only collect amount (no source/income fields)
const AMOUNT_ONLY_TYPES = new Set(
  HOUSEHOLD_ASSETS_QUESTIONS.filter((q) => q.amountOnly).map((q) => q.name)
)

const validationSchema = Yup.object({
  householdAssets: Yup.array().of(
    Yup.object({
      applies: Yup.boolean().required('An answer is required'),
      source: Yup.string().when(['applies', 'shortenedType'], {
        is: (applies, shortenedType) =>
          applies === true && !AMOUNT_ONLY_TYPES.has(shortenedType),
        then: (schema) => schema.required('Bank is required'),
      }),
      income: Yup.string().when(['applies', 'shortenedType'], {
        is: (applies, shortenedType) =>
          applies === true && !AMOUNT_ONLY_TYPES.has(shortenedType),
        then: (schema) => schema.required('Income is required'),
      }),
      amount: Yup.string().when('applies', {
        is: true,
        then: (schema) => schema.required('Amount is required'),
      }),
      shortenedType: Yup.string(),
    })
  ),
})

function AssetGroups() {
  const { values, setFieldValue } = useFormikContext()

  return (
    <div className="asset-groups">
      {HOUSEHOLD_ASSETS_GROUPS.map((group, groupIndex) => {
        const groupQuestions = HOUSEHOLD_ASSETS_QUESTIONS.filter((q) =>
          group.questionNames.includes(q.name)
        )
        const groupAssets = values.householdAssets.filter((a) =>
          group.questionNames.includes(a.shortenedType)
        )
        const isSectionComplete =
          groupAssets.length > 0 &&
          groupAssets.every(
            (a) =>
              a.applies !== null &&
              a.applies !== undefined &&
              a.applies !== ''
          )
        const stepNumber = String(groupIndex + 1).padStart(2, '0')
        const isLastGroup = groupIndex === HOUSEHOLD_ASSETS_GROUPS.length - 1

        const handleNoToAll = () => {
          group.questionNames.forEach((name) => {
            const idx = findIndex(
              values.householdAssets,
              (a) => a.shortenedType === name
            )
            if (idx !== -1) {
              setFieldValue(`householdAssets[${idx}].applies`, false)
            }
          })
        }

        return (
          <div key={group.header} className="asset-group-box">
            <div className="asset-group-header">
              <div
                className={classNames('step-number-box', {
                  complete: isSectionComplete,
                })}
              >
                {isSectionComplete ? (
                  <img
                    className="checkmark"
                    src={checkmark}
                    alt={`${group.header} is complete`}
                  />
                ) : (
                  stepNumber
                )}
              </div>
              <span className="asset-group-title">{group.header}</span>
            </div>

            <div className="asset-group-body">
              {isLastGroup && (
                <div style={{ marginBottom: '16px' }}>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={handleNoToAll}
                  >
                    No to All
                  </button>
                </div>
              )}
              <FieldArray name="householdAssets">
                <div>
                  {groupQuestions.map(
                    ({ name, label, assetLabels = {}, amountOnly = false }) => {
                      // Use a predicate function to guarantee we find the exact
                      // asset by shortenedType — avoids lodash _.matches quirks
                      // when multiple assets share similar field values.
                      const assetValueIndex = findIndex(
                        values.householdAssets,
                        (a) => a.shortenedType === name
                      )
                      const assetValue =
                        assetValueIndex !== -1
                          ? values.householdAssets[assetValueIndex]
                          : null
                      const globalIndex = findIndex(
                        HOUSEHOLD_ASSETS_QUESTIONS,
                        (q) => q.name === name
                      )
                      return (
                        <AssetField
                          key={name}
                          label={label}
                          applies={assetValue?.applies}
                          truvValidated={assetValue?.truvValidated}
                          assetIndex={assetValueIndex}
                          questionIndex={globalIndex}
                          assetLabels={assetLabels}
                          amountOnly={amountOnly}
                        />
                      )
                    }
                  )}
                </div>
              </FieldArray>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  previousStep: PropTypes.object.isRequired,
}

const defaultProps = {}

function HouseholdAssetsForm({ onSubmit, initialValues, previousStep }) {
  return (
    <StepForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      previousStep={previousStep}
    >
      {() => <AssetGroups />}
    </StepForm>
  )
}

HouseholdAssetsForm.propTypes = exact(propTypes)
HouseholdAssetsForm.defaultProps = defaultProps

export default React.memo(HouseholdAssetsForm)
